/**
 * WorldMap — the static map contract between @sa/mapdata (producer) and the
 * sim / server / client / bots (consumers). See docs/GAME_DESIGN.md §0, §1.2, §6.
 *
 * Coordinates are map units (mu): 1 mu = 1° latitude.
 *   x = ((lon + 169) mod 360) * cos(30°)   → 0 … ~311.8
 *   y = 78 − lat                            → 0 … 136
 * Land hexes are indexed 0..N-1 in row-major (r, q) order; water hexes 0..M-1 likewise.
 * This file holds only types + pure helpers (no fs / fetch).
 */

export type HexIdx = number;
export type WaterIdx = number;
export type RegionId = number; // 1..8
export type CountryId = number; // 0 = none

export interface Vec2 {
  x: number;
  y: number;
}

export interface MapRegion {
  id: RegionId;
  name: string; // e.g. "EUROPA"
  anchor: Vec2;
  capitalHex: HexIdx;
  townHexes: HexIdx[]; // 2 L1 towns
  coreHexes: HexIdx[]; // ~50 hexes, BFS from anchor
}

export interface MapCity {
  name: string; // uppercase, ≤10 chars
  hex: HexIdx;
  level: number; // 1..5 initial level (neutral cities are capture prizes)
  pop: number; // real pop_max, informational
  region: RegionId | 0; // core region it belongs to, else 0
}

export interface MapCountry {
  id: CountryId;
  name: string;
}

export interface WorldMap {
  mapVersion: string;
  /** hex circumradius in mu (pointy-top axial) */
  hexR: number;
  width: number; // mu
  height: number; // mu

  // ---- land hexes (N) ----
  landCount: number;
  q: Int16Array;
  r: Int16Array;
  cx: Float32Array;
  cy: Float32Array;
  capBase: Float32Array; // 3..60
  areaW: Float32Array; // latitude area weight
  countryId: Uint8Array;
  coreOf: Uint8Array; // 0 or region id
  coastal: Uint8Array; // 0/1
  /** land neighbours, N*6, -1 = none */
  nbr: Int32Array;
  /** CSR sea links / straits: links of hex i are linkHex[linkStart[i] .. linkStart[i+1]) */
  linkStart: Int32Array; // N+1
  linkHex: Int32Array;
  linkW: Float32Array; // 0.3 typical
  /** adjacent water hex for ports, -1 if not coastal */
  portWater: Int32Array;

  // ---- water / naval nav grid (M) ----
  waterCount: number;
  wq: Int16Array;
  wr: Int16Array;
  wcx: Float32Array;
  wcy: Float32Array;
  /** water neighbours, M*6, -1 = none (includes forced canal links Suez/Panama) */
  wnbr: Int32Array;

  // ---- metadata ----
  regions: MapRegion[]; // 8
  cities: MapCity[];
  countries: MapCountry[];

  // ---- render-only geometry (client) ----
  /** coastline polylines: flat [x0,y0,x1,y1,...] per ring, in mu */
  coast: Float32Array[];
  /** faint country border polylines, same encoding */
  borders: Float32Array[];
}

export const MAP_LAT_MIN = -58;
export const MAP_LAT_MAX = 78;
export const MAP_LON_CUT = -169; // Bering Strait cut
export const COS30 = Math.cos(Math.PI / 6);

export function lonLatToMap(lon: number, lat: number): Vec2 {
  const l = (((lon - MAP_LON_CUT) % 360) + 360) % 360;
  return { x: l * COS30, y: MAP_LAT_MAX - lat };
}

export function mapToLonLat(x: number, y: number): { lon: number; lat: number } {
  let lon = x / COS30 + MAP_LON_CUT;
  if (lon > 180) lon -= 360;
  return { lon, lat: MAP_LAT_MAX - y };
}
